import React, { createContext, useContext, useState, ReactNode, useCallback } from "react";
import { Snackbar, Alert, AlertColor } from "@mui/material";

export type NotificationType = {
    message: string;
    type: AlertColor;
    duration?: number;
};

interface NotificationContextType {
    showNotification: (notification: NotificationType) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
    children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
    const [open, setOpen] = useState(false);
    const [notification, setNotification] = useState<NotificationType>({
        message: "",
        type: "info",
        duration: 10000,
    });

    const showNotification = useCallback((newNotification: NotificationType) => {
        setNotification({
            message: newNotification.message,
            type: newNotification.type,
            duration: newNotification.duration || 10000,
        });
        setOpen(true);
    }, []);

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") {
            return;
        }
        setOpen(false);
    };

    return (
        <NotificationContext.Provider value={{ showNotification }}>
            {children}
            <Snackbar
                open={open}
                autoHideDuration={notification.duration}
                onClose={handleClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={handleClose}
                    severity={notification.type}
                    sx={{
                        width: "100%",
                        fontSize: "1rem",
                        px: 3,
                        py: 2,
                        border: 1,
                        borderColor: (theme) => theme.palette[notification.type].main,
                        color: (theme) => theme.palette[notification.type].main,
                        "& .MuiAlert-icon": {
                            fontSize: "1.4rem",
                        },
                    }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </NotificationContext.Provider>
    );
};

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error("useNotification must be used within a NotificationProvider");
    }
    return context;
};
